import { useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Accordion, Button, Divider, Icon, Label, Message, Segment, Tab, Table, TabPane } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery } from "api"
import { UserChip } from "components/lib/Chips"
import { MultiLineParagraph } from "components/lib/UI"
import { useAppSelector } from "store"
import util from "utils/utils"

type IdeaAssessmentReportProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  challenge: OpenAPI.GET<"/challenges/{id}">["response"]
  removeAssessors?: (ideaIds: string[], userIds: string[], assessmentRoundId: string) => void
  onRemoveExternalInvitation?: (ideaId: string, invitation: OpenAPI.Schemas["Invitation"]) => void
  fetchIdeas?: () => void
  fixedHeight?: number | null
}

const IdeaAssessmentReport = ({
  challenge,
  idea,
  removeAssessors,
  onRemoveExternalInvitation,
  fetchIdeas,
  fixedHeight = 400,
}: IdeaAssessmentReportProps) => {
  const user = useAppSelector((state) => state.user)
  const { t } = useTranslation()

  const [selectedAssessor, setSelectedAssessor] = useState("")

  const template = challenge?.ownerIdeaTemplate
  const assessmentRounds = template?.assessments?.rounds || []
  const [selectedAssessmentRoundId, setSelectedAssessmentRoundId] = useState(assessmentRounds?.at(0)?.id || "legacy")

  // Associate a given assessment ID with its title
  const getAssessmentTitle = (assessmentId) => {
    const assessmentSections = challenge?.ownerIdeaTemplate?.body.filter((section) => section.type === "assessment")
    const assessmentFields = assessmentSections.reduce((acc, section) => acc.concat(section.fields), [])
    const field = assessmentFields.find((f) => f.id === assessmentId)
    return field?.title || "Unknown field"
  }

  const invitationQuery = useQuery({
    queryKey: ["invitations", "ideaAssessor", idea.challenge],
    queryFn: asQuery(api.invitations.getForType, {
      params: ["ideaAssessor", idea.challenge],
    }),
  })

  const removeAssessor = (assessment) => {
    removeAssessors([idea._id], [assessment?.user], selectedAssessmentRoundId)
  }

  const removeInvite = (invitation) => {
    util
      .confirm(t("ideas.invitations.remove.title"), t("ideas.invitations.remove.info_assessor"))
      .then(() => {
        api.invitations.remove(
          invitation._id,
          () => {
            api.queryClient.setQueryData(
              ["invitations", "ideaAssessor", idea.challenge],
              (oldData: OpenAPI.GET<"/invitations">["response"] | undefined) => {
                if (!oldData) return oldData
                return {
                  ...oldData,
                  invitations: oldData.invitations.filter((i) => i._id !== invitation._id),
                }
              },
            )

            toast.success("Invite removed")
            onRemoveExternalInvitation(idea._id, invitation)
          },
          (err) => toast.error(err.message),
        )
      })
      .catch(() => {})
  }

  const unsubmitAssessment = (assessment) => {
    api.ideas.unsubmitAssessment(
      idea._id,
      selectedAssessmentRoundId,
      assessment.user,
      () => {
        toast.success("Assessment unsubmitted")
        fetchIdeas?.()
      },
      (err) => toast.error(err.message),
    )
  }

  const ideaId = idea._id
  const invited = useMemo(
    () =>
      invitationQuery.data?.invitations
        .filter((i) => i.forIdeas?.includes(ideaId))
        .map((i) => ({
          ...i,
          profile: {
            firstName: i.inviteeUser?.profile?.firstName || i.invitee,
            created: `Invited ${moment(i.createdAt).format("DD/MM/YY, hh:mm A")}`,
          },
        })),
    [invitationQuery.data, ideaId],
  )

  return (
    <div data-testid="simplydo-idea-assessment-report" style={{ maxHeight: fixedHeight || "auto", overflow: "auto" }}>
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={assessmentRounds.findIndex((r) => r.id === selectedAssessmentRoundId)}
        onTabChange={(_, data) => {
          const round = assessmentRounds[data.activeIndex || 0]
          setSelectedAssessmentRoundId(round.id)
          setSelectedAssessor("")
        }}
        panes={assessmentRounds.map((round) => {
          const ideaScores = idea?.assessmentDetails?.assessmentRounds?.find((r) => r.id === round.id)

          return {
            menuItem: round.name,
            render: () => (
              <div key={round.id}>
                <h4>
                  Total assessment score
                  <Label style={{ marginLeft: 10 }} color="violet">
                    {Math.round(ideaScores?.averageSubmittedScore || 0)}%
                  </Label>
                </h4>
                <Accordion>
                  {idea.assessments?.map((a) => {
                    const isSelected = selectedAssessor === a.user
                    if ((a.assessmentRoundId || "legacy") !== round.id) return null
                    return (
                      <>
                        <Accordion.Title
                          active={isSelected}
                          onClick={() => setSelectedAssessor(isSelected ? "" : a.user)}
                        >
                          <Icon name={isSelected ? "chevron down" : "chevron right"} />
                          {a?.assessorUser ? (
                            <UserChip
                              user={a.assessorUser}
                              onClick={isSelected ? null : () => setSelectedAssessor(a.assessorUser?._id)}
                            />
                          ) : (
                            <span>Unknown user</span>
                          )}
                          {a.conflictOfInterest?.hasConflictOfInterest ? (
                            <Label size="small" color="red" style={{ marginLeft: 10, marginTop: 5 }}>
                              <Icon name="warning sign" />
                              Conflict of interest
                            </Label>
                          ) : null}
                          {!a.conflictOfInterest?.hasConflictOfInterest && (
                            <Label size="small" color="violet" style={{ marginLeft: 10, marginTop: 5 }}>
                              {Math.round(a.score || 0)}%
                            </Label>
                          )}
                          {a.isSubmitted ? (
                            <Label size="small" color="green" style={{ marginLeft: 10, marginTop: 5 }}>
                              Submitted
                            </Label>
                          ) : (
                            <Label size="small" color="orange" style={{ marginLeft: 10, marginTop: 5 }}>
                              Not yet submitted
                            </Label>
                          )}
                        </Accordion.Title>
                        <Accordion.Content active={isSelected}>
                          {a.conflictOfInterest?.hasConflictOfInterest && a.conflictOfInterest?.notes ? (
                            <Message
                              error
                              size="tiny"
                              header="Conflict of interest note"
                              content={a.conflictOfInterest.notes}
                            />
                          ) : (
                            Object.entries(a.assessment || {}).map(([fieldId, fieldAssessment]) => (
                              <Segment attached key={fieldId}>
                                <h4 style={{ marginBottom: 0 }}>
                                  {getAssessmentTitle(fieldId)}{" "}
                                  {fieldAssessment.score ? <Label>Score: {fieldAssessment.score || 0}</Label> : null}
                                </h4>
                                <MultiLineParagraph>
                                  {fieldAssessment.notes || <i>No comments left</i>}
                                </MultiLineParagraph>
                              </Segment>
                            ))
                          )}
                          {util.isEmpty(a.assessment) && !a.conflictOfInterest?.hasConflictOfInterest ? (
                            <Message size="tiny" style={{ marginBottom: 12 }}>
                              This assessor has yet to begin their assessment.
                            </Message>
                          ) : null}
                          {(util.canManageChallenge(user, challenge) ||
                            util.hasPermission(user, "challenge.editAssessments", idea.challenge)) &&
                          removeAssessors ? (
                            <div
                              style={{
                                marginTop: 5,
                                display: "flex",
                                justifyContent: "flex-end",
                                marginBottom: 5,
                              }}
                            >
                              <Button
                                basic
                                size="mini"
                                style={{ marginBottom: 10 }}
                                onClick={() => removeAssessor(a)}
                                icon="trash"
                                content="Remove assessor"
                              />
                              {a.isSubmitted ? (
                                <Button
                                  basic
                                  size="mini"
                                  style={{ marginBottom: 10, marginLeft: 5 }}
                                  onClick={() => unsubmitAssessment(a)}
                                  icon="ban"
                                  content="Unsubmit assessment"
                                />
                              ) : null}
                            </div>
                          ) : null}
                        </Accordion.Content>
                      </>
                    )
                  })}
                </Accordion>
              </div>
            ),
          }
        })}
      />
      {invited?.length > 0 && (
        <>
          <Divider section />
          <h5 style={{ marginRight: 16 }}>Pending invitations</h5>
          <Table basic="very" relaxed compact="very">
            <Table.Body>
              {invited.map((a) => {
                const assignedRoundIds = a.ideaDetails?.[idea._id]?.assessmentRounds || ["legacy"]
                const assignedRounds = assignedRoundIds.map(
                  (id) => assessmentRounds.find((r) => r.id === id)?.name || "Unknown round",
                )

                return (
                  <Table.Row key={a._id}>
                    <Table.Cell>{a.inviteeUser?.profile?.firstName || a.invitee}</Table.Cell>
                    <Table.Cell>{`${assignedRounds.join(", ")}`}</Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      {util.canManageChallenge(user, challenge) ||
                      util.hasPermission(user, "challenge.editAssessments", idea.challenge) ? (
                        <Button size="tiny" basic icon="trash" onClick={() => removeInvite(a)} />
                      ) : null}
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table>
        </>
      )}
    </div>
  )
}

export default IdeaAssessmentReport
